<template>
    <div class="tw:flex tw:flex-col tw:gap-4">

        <!-- ── Indicador de paso (stepper visual) ── -->
        <Stepper
            :steps="pasoLabels"
            :current-step="pasoActual"
            class="tw:px-2"
        />

        <!-- ── Timer de sesión ── -->
        <!-- El timer arranca en el Paso 1 (Zonas)  -->
        <CountdownBar
            v-if="sessionEndsAt"
            :ends-at="sessionEndsAt"
            :label="$t('perfil_invitaciones.tiempo_restante')"
            :auto-start="true"
            :urgency-threshold-ms="60000"
            size="sm"
            class="tw:bg-white tw:border tw:border-gray-200 tw:rounded-lg tw:font-light"
            @expired="handleTimerExpirado"
        />

        <!-- ── Modal informativo: últimos 5 minutos ── -->
        <BaseModalFront
            :show="showModalAvisoTiempo"
            :show-cancel="false"
            :confirm-text="$t('partido.tiempo_modal_cerrar')"
            confirm-variant="alt"
            :close-on-backdrop="true"
            :close-on-escape="true"
            @confirm="showModalAvisoTiempo = false"
            @close="showModalAvisoTiempo = false"
        >
            <template #header>
                <div class="tw:flex tw:items-center tw:gap-2">
                    <i class="material-icons tw:text-orange-600 tw:text-2xl">schedule</i>
                    <h2 class="tw:text-xl tw:font-semibold tw:text-orange-600 tw:m-0">
                        {{ $t('perfil_invitaciones.tiempo_aviso_titulo') }}
                    </h2>
                </div>
            </template>
            <div class="tw:flex tw:items-center tw:gap-2 tw:mb-3">
                <span class="tw:text-lg tw:font-bold tw:text-orange-600">
                    {{ $t('perfil_invitaciones.tiempo_aviso_restante') }}
                </span>
                <span class="tw:text-lg tw:font-mono tw:font-bold tw:text-orange-600">
                    {{ countdownMonitor.formatted.value }}
                </span>
            </div>
            <p class="tw:text-sm tw:text-gray-600 tw:m-0">
                {{ $t('perfil_invitaciones.tiempo_aviso_info') }}
            </p>
        </BaseModalFront>

        <!-- ── Modal: tiempo agotado ── -->
        <BaseModalFront
            :show="showModalTiempoAgotado"
            :show-cancel="false"
            :confirm-text="$t('partido.tiempo_modal_cerrar')"
            confirm-variant="alt"
            :close-on-backdrop="false"
            :close-on-escape="false"
            @confirm="cerrarModalTiempoAgotado"
            @close="cerrarModalTiempoAgotado"
        >
            <template #header>
                <div class="tw:flex tw:items-center tw:gap-2">
                    <i class="material-icons tw:text-red-600 tw:text-2xl">timer_off</i>
                    <h2 class="tw:text-xl tw:font-semibold tw:text-red-600 tw:m-0">
                        {{ $t('perfil_invitaciones.tiempo_agotado_titulo') }}
                    </h2>
                </div>
            </template>
            <p class="tw:text-sm tw:text-gray-600 tw:m-0">
                {{ $t('perfil_invitaciones.tiempo_agotado_info') }}
            </p>
        </BaseModalFront>

        <!-- ── Contenido de cada paso ── -->
        <InvitacionesPasoZona
            v-if="pasoActual === PASO_ZONA"
            :zonas="zonas"
            :loading="loadingZonas"
            :error="errorZonas"
            :zona-seleccionada="zonaSeleccionada"
            :svg-content="svgRecinto"
            @seleccionar-zona="seleccionarZona"
        />

        <InvitacionesPasoButacas
            v-else-if="pasoActual === PASO_BUTACAS"
            :asientos-data="asientosData"
            :loading-asientos="loadingAsientos"
            :error-asientos="errorAsientos"
            :error-bloquear="errorBloquear"
            :asientos-seleccionados="asientosSeleccionados"
            :max-asientos="maxAsientos"
            :zona-seleccionada="zonaSeleccionada"
            @cambiar-zona="prevStep"
            @toggle-asiento="toggleAsiento"
            @quitar-asiento="quitarAsiento"
        />

        <InvitacionesPasoDatos
            v-else-if="pasoActual === PASO_DATOS"
            ref="pasoDatosRef"
            :form="formDatosComprador"
            :form-custom="formCamposCustom"
            :datos-por-butaca="formDatosPorButaca"
            :campos-custom="eventoFields"
            :teatro-hash="teatroHash"
            :error="errorGuardarDatos"
            :pago-requerido="pagoRequerido"
            @update:form="updateFormDatosComprador"
            @update:form-custom="updateFormCamposCustom"
            @update:datos-por-butaca="updateDatosPorButaca"
        />

        <InvitacionesPasoPago
            v-else-if="pasoActual === PASO_PAGO"
            :jet_id="jetIdResult"
            :formulario="formularioResult"
            :session_id="sessionId"
            :amount="amountResult"
            @completado="handlePagoCompletado"
            @cancelar="prevStep"
        />

        <InvitacionesPasoResumen
            v-else-if="pasoActual === PASO_RESUMEN"
            :evento="evento"
            :asientos-seleccionados="asientosSeleccionados"
            :form="formDatosComprador"
            :datos-por-butaca="formDatosPorButaca"
            :zona-seleccionada="zonaSeleccionada"
            :amount="amountResult"
            :terminos-aceptados="terminosAceptados"
            :mostrar-errores="mostrarErroresTerminos"
            :pago-requerido="pagoRequerido"
            :loading-procesar="loadingProcesar"
            :error="errorProcesar"
            @update:terminos-aceptados="onUpdateTerminos"
        />

        <!-- ── Botones de navegación ── -->
        <div class="tw:flex tw:items-center tw:justify-between tw:gap-3">
            <!-- Botón Atrás -->
            <Button
                v-if="pasoActual > PASO_ZONA"
                type="button"
                variant="alt"
                :disabled="loadingBloquear || loadingGuardarDatos || loadingProcesar"
                @click="prevStep"
            >
                <span class="tw:flex tw:items-center tw:gap-1">
                    <i class="material-icons tw:text-base">arrow_back</i>
                    {{ $t('perfil_invitaciones.atras') }}
                </span>
            </Button>

            <!-- Botón Siguiente / Finalizar / Ir a pagar -->
            <Button
                v-if="!(pasoActual === PASO_ZONA || pasoActual === PASO_PAGO)"
                type="button"
                variant="alt"
                :disabled="loadingBloquear || loadingGuardarDatos || loadingProcesar || paymentLoading || (pasoActual === PASO_RESUMEN && !puedeFinalizarPasoResumen)"
                @click="handleNext"
            >
                <span class="tw:flex tw:items-center tw:h-6" v-if="loadingBloquear || loadingGuardarDatos || loadingProcesar">
                    {{ $t('perfil_invitaciones.procesando') }}…
                </span>
                <span class="tw:flex tw:items-center tw:gap-1" v-else-if="pasoActual === PASO_RESUMEN && pagoRequerido">
                    {{ $t('perfil_invitaciones.siguiente') }}
                    <i class="material-icons tw:text-base">arrow_forward</i>
                </span>
                <span class="tw:flex tw:items-center tw:h-6" v-else-if="pasoActual === PASO_RESUMEN">
                    {{ $t('perfil_invitaciones.finalizar') }}
                </span>
                <span class="tw:flex tw:items-center tw:gap-1" v-else>
                    {{ $t('perfil_invitaciones.siguiente') }}
                    <i class="material-icons tw:text-base">arrow_forward</i>
                </span>
            </Button>
        </div>
    </div>
</template>

<script setup>
    import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
    import {
        useGenerarInvitaciones,
        PASO_ZONA,
        PASO_BUTACAS,
        PASO_DATOS,
        PASO_RESUMEN,
        PASO_PAGO,
    } from '@/composables/front/perfil/useGenerarInvitaciones';
    import { usePerfil } from '@/composables/front/perfil/usePerfil';
    import { useToast } from '@/composables/common/useToast';
    import { useCountdown } from '@/composables/common/useCountdown';
    import { t } from '@/utils/i18n-helper';
    import { config } from '@/config';
    import CountdownBar from '@/modules/front/ui/CountdownBar.vue';
    import Stepper from '@/modules/front/ui/Stepper.vue';
    import InvitacionesPasoZona from '../components/invitaciones/InvitacionesPasoZona.vue';
    import InvitacionesPasoButacas from '../components/invitaciones/InvitacionesPasoButacas.vue';
    import InvitacionesPasoDatos from '../components/invitaciones/InvitacionesPasoDatos.vue';
    import InvitacionesPasoResumen from '../components/invitaciones/InvitacionesPasoResumen.vue';
    import InvitacionesPasoPago from '../components/invitaciones/InvitacionesPasoPago.vue';
    import BaseModalFront from '@/modules/front/ui/BaseModalFront.vue';
    import Button from '@/modules/front/ui/Button.vue';

    const props = defineProps({
        abonoId: {
            type: [String, Number],
            required: true,
        },
        eventoInicial: {
            type: Object,
            required: true,
        },
        /** Invitaciones restantes del abonado (para calcular maxAsientos) */
        statsRestantes: {
            type: Number,
            default: null,
        },
        /**
         * Datos del abonado autenticado para precargar el formulario del Paso 3.
         * Acepta la forma del backend: { name, surnames, email, telefono, dni, cp, edad, ... }
         */
        datosAbonado: {
            type: Object,
            default: null,
        },
    });

    const emit = defineEmits([
        /** Emitido cuando la compra se procesó con éxito. Payload: { session_id, n_pedido } */
        'completado',
        /**
         * Emitido cuando el usuario cancela el flujo o el timer expira.
         * El padre debe volver al dashboard de invitaciones.
         */
        'cancelado',
    ]);

    const {
        pasoActual,
        evento,

        zonas,
        loadingZonas,
        errorZonas,
        zonaSeleccionada,
        svgRecinto,

        asientosData,
        loadingAsientos,
        errorAsientos,
        asientosSeleccionados,
        maxAsientos,
        loadingBloquear,
        errorBloquear,

        sessionId,
        formDatosComprador,
        formCamposCustom,
        formDatosPorButaca,
        teatroHash,
        loadingGuardarDatos,
        errorGuardarDatos,
        amountResult,
        jetIdResult,
        formularioResult,
        pagoRequerido,

        terminosAceptados,
        loadingProcesar,
        errorProcesar,
        resultadoCompra,

        puedeAvanzarPasoZona,
        puedeAvanzarPasoButacas,
        puedeAvanzarPasoDatos,
        puedeFinalizarPasoResumen,

        iniciar,
        setStatsRestantes,
        seleccionarZona,
        toggleAsiento,
        quitarAsiento,
        prevStep,
        nextStep,
        onTimerExpirado,
        procesarCompra,
        paymentLoading,
    } = useGenerarInvitaciones(() => props.abonoId);

    const toast = useToast();

    // Perfil del usuario autenticado como fuente de fecha_nacimiento
    const { perfil, getPerfil } = usePerfil();

    let iniciado = false;

    /**
     * Convierte fecha de DD/MM/YYYY a YYYY-MM-DD (formato input type="date").
     * Si ya está en YYYY-MM-DD o no es válida, la devuelve tal cual.
     */
    const toDateInputFormat = fecha => {
        if (!fecha) return '';
        // Ya en formato ISO
        if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) return fecha;
        // Formato DD/MM/YYYY
        const match = fecha.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (match) return `${match[3]}-${match[2]}-${match[1]}`;
        return fecha;
    };

    const iniciarFlujo = async() => {
        if (iniciado) return;
        iniciado = true;

        // Garantizar que perfil.value tiene datos (fecha_nacimiento vive aquí)
        if (!perfil.value) {
            await getPerfil();
        }

        const datosAbonadoMerged = props.datosAbonado ? {
            ...props.datosAbonado,
            fecha_nacimiento: toDateInputFormat(
                props.datosAbonado.fecha_nacimiento?.trim()
                || props.datosAbonado.fechaDeNacimiento?.trim()
                || props.datosAbonado.birth_date?.trim()
                || perfil.value?.fecha_nacimiento
                || ''
            ),
        } : null;

        iniciar(
            props.eventoInicial,
            props.statsRestantes !== null ? { restantes: props.statsRestantes } : null,
            datosAbonadoMerged,
        );
        if (props.statsRestantes !== null) setStatsRestantes(props.statsRestantes);
    };

    // ── Liberar sesión al recargar/cerrar pestaña ──────────────────────────
    const onBeforeUnload = () => {
        if (!sessionId.value) return;
        const token = localStorage.getItem('token');
        const url = `${config.API_BASE}/f/sesion/${sessionId.value}?api_key=${token}`;
        fetch(url, {
            method: 'DELETE',
            keepalive: true,
            headers: {
                'Authorization': token,
                'Autrollization': token,
                'Hash-Teatro': localStorage.getItem('hash_teatro') || '',
            },
        });
    };

    onMounted(() => {
        window.scrollTo(0, 0);
        window.addEventListener('beforeunload', onBeforeUnload);
        iniciarFlujo();
    });

    onUnmounted(() => {
        window.removeEventListener('beforeunload', onBeforeUnload);
    });

    // Campos custom del evento (puede ser undefined si el evento no los tiene)
    const eventoFields = computed(() =>
        (evento.value && Array.isArray(evento.value.fields)) ? evento.value.fields : []
    );

    // Etiquetas de los pasos para el Stepper (dinámico: 4 pasos si no hay pago, 5 si pago requerido)
    const pasoLabels = computed(() => {
        const labels = ['Zona', 'Butacas', 'Datos', 'Resumen'];
        if (pagoRequerido.value) labels.push(t('perfil_invitaciones.pago_titulo'));
        return labels;
    });

    /** ¿Puede avanzar el usuario desde el paso actual? */
    const canAdvance = computed(() => {
        switch (pasoActual.value) {
            case PASO_ZONA:    return puedeAvanzarPasoZona.value;
            case PASO_BUTACAS: return puedeAvanzarPasoButacas.value;
            case PASO_DATOS:   return puedeAvanzarPasoDatos.value;
            case PASO_RESUMEN: return puedeFinalizarPasoResumen.value;
            default:           return false;
        }
    });

    /**
     * Timer de sesión: se fija UNA sola vez al arrancar el flujo (iniciar()).
     * CountdownBar visible desde Paso 1. Monitor para modal de aviso a 5 min.
     */
    const SESSION_DURATION_MS = 15 * 60 * 1000;
    const FIVE_MINUTES_MS = 5 * 60 * 1000;
    const sessionEndsAt = ref(new Date(Date.now() + SESSION_DURATION_MS));

    // Monitor de countdown para el modal de aviso (independiente del CountdownBar visual)
    const countdownMonitor = useCountdown({
        endsAt: sessionEndsAt.value,
        autoStart: true,
    });

    // Modal de aviso a 5 minutos (solo informativo, no afecta al timer)
    const showModalAvisoTiempo = ref(false);
    let avisoTiempoMostrado = false;

    watch(() => countdownMonitor.totalMsLeft.value, msLeft => {
        if (msLeft <= FIVE_MINUTES_MS && msLeft > 0 && !avisoTiempoMostrado) {
            avisoTiempoMostrado = true;
            showModalAvisoTiempo.value = true;
        }
    });

    // Modal de tiempo agotado
    const showModalTiempoAgotado = ref(false);

    const handleTimerExpirado = async() => {
        await onTimerExpirado();
        sessionEndsAt.value = null;
        showModalAvisoTiempo.value = false;
        showModalTiempoAgotado.value = true;
    };

    const cerrarModalTiempoAgotado = () => {
        showModalTiempoAgotado.value = false;
        emit('cancelado');
    };

    // ── Pago completado ────────────────────────────────────────────────────
    const handlePagoCompletado = ({ n_pedido, session_id }) => {
        toast.success(t('perfil_invitaciones.invitacion_generada', {}, 'Invitación generada correctamente'));
        sessionEndsAt.value = null;
        window.location.href = `/compra/${session_id}/${n_pedido}`;
    };

    // ── Refs de componentes ──────────────────────────────────────────────────
    const pasoDatosRef = ref(null);

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleNext = async() => {
        if (pasoActual.value === PASO_RESUMEN) {
            if (!puedeFinalizarPasoResumen.value) {
                mostrarErroresTerminos.value = true;
                return;
            }
            if (pagoRequerido.value) {
                await nextStep();
            } else {
                await procesarCompra();
                if (resultadoCompra.value) {
                    toast.success(t('perfil_invitaciones.invitacion_generada', {}, 'Invitación generada correctamente'));
                    // Flujo completado: ocultar el timer
                    sessionEndsAt.value = null;
                    emit('completado', resultadoCompra.value);
                }
            }
        } else if (pasoActual.value === PASO_DATOS) {
            // Intentar avanzar — nextStep() internamente valida puedeAvanzarPasoDatos
            await nextStep();
            // Si no avanzó (campos incompletos), forzar errores visuales en el formulario
            if (pasoActual.value === PASO_DATOS) {
                pasoDatosRef.value?.validate();
            }
        } else {
            await nextStep();
        }
    };

    // Bindings reactivos de form (los componentes hijos emiten nuevos objetos completos)
    const updateFormDatosComprador = val => { formDatosComprador.value = val; };
    const updateFormCamposCustom   = val => { formCamposCustom.value   = val; };
    const updateDatosPorButaca     = val => { formDatosPorButaca.value = val; };
    const onUpdateTerminos         = val => {
        terminosAceptados.value  = val;
        mostrarErroresTerminos.value = false;
    };

    // ── Estado local de UI ─────────────────────────────────────────────────────
    /** Activa los mensajes de error de términos tras intentar finalizar sin aceptarlos */
    const mostrarErroresTerminos = ref(false);
</script>
